import { Fragment, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isHomeNavSection, NAV_ITEMS } from '../../constants/navItems';
import { HomeScenarioSubNav } from '../nav/HomeScenarioSubNav';
import {
  parseSettingsCategory,
  SETTINGS_CATEGORIES,
  settingsPath,
} from '../../constants/settings/settingsCategories';
import { useOpenApp } from '../../hooks/useOpenApp';
import { Icon } from '../header/draft/Icon';
import './mobile-nav-drawer.css';

type MobileNavDrawerProps = {
  open: boolean;
  onClose: () => void;
};

const drawerNavBase =
  'soundpill-mobile-nav-drawer__nav-link flex items-center gap-4 px-4 py-3.5 rounded-xl font-headline text-base no-underline';

const drawerNavInactive =
  'text-on-surface-variant font-medium hover:bg-white/5 hover:text-white';

const drawerNavActive = 'soundpill-mobile-nav-drawer__nav-link--active text-white font-bold bg-primary/10';

const drawerSubNavBase =
  'soundpill-mobile-nav-drawer__sub-link flex items-center gap-3 px-3 py-2.5 rounded-lg font-headline text-sm no-underline';

const drawerSubNavInactive =
  'text-on-surface-variant/90 font-medium hover:bg-white/5 hover:text-white';

const drawerSubNavActive =
  'soundpill-mobile-nav-drawer__sub-link--active text-primary font-semibold bg-primary/10';

function isNavItemActive(
  pathname: string,
  item: (typeof NAV_ITEMS)[number],
  navActive: boolean,
): boolean {
  if (item.matchPrefix) {
    return pathname.startsWith(item.matchPrefix);
  }
  return navActive;
}

/** lg 미만 — Draft MobileNavDrawer */
export function MobileNavDrawer({ open, onClose }: MobileNavDrawerProps) {
  const { t } = useTranslation();
  const openApp = useOpenApp();
  const { pathname, search } = useLocation();
  const settingsTab = parseSettingsCategory(new URLSearchParams(search).get('tab'));
  const onSettingsPage = pathname.startsWith('/app/setting');
  const onHomeSection = isHomeNavSection(pathname);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <AnimatePresence>
      {open ? (
        <>
          <motion.button
            type="button"
            aria-label={t('layout.closeMenu')}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[70] bg-black/55 backdrop-blur-sm lg:hidden"
            onClick={onClose}
          />
          <motion.aside
            role="dialog"
            aria-modal="true"
            aria-label={t('layout.menu')}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 380, damping: 36 }}
            className="soundpill-mobile-nav-drawer fixed top-0 right-0 bottom-0 z-[80] w-[min(300px,88vw)] flex flex-col bg-surface-container-lowest/95 backdrop-blur-3xl shadow-[-12px_0_48px_rgba(0,0,0,0.45)] lg:hidden"
          >
            <div className="soundpill-mobile-nav-drawer__header flex items-center justify-between h-topbar-height px-4 shrink-0">
              <span className="font-headline text-lg font-bold text-white">{t('layout.menu')}</span>
              <button
                type="button"
                onClick={onClose}
                className="p-2 rounded-lg text-on-surface-variant hover:text-white hover:bg-white/5 transition-colors"
                aria-label={t('common.close')}
              >
                <Icon name="close" className="text-2xl" />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto px-4 py-5 flex flex-col gap-2">
              {NAV_ITEMS.map((item) => (
                <Fragment key={item.to}>
                  {item.dividerBefore ? (
                    <div className="soundpill-mobile-nav-drawer__divider my-3" aria-hidden />
                  ) : null}
                  <NavLink
                    to={item.to}
                    end={item.end}
                    onClick={onClose}
                    className={({ isActive }) => {
                      const active =
                        item.to === '/app/setting' && onSettingsPage
                          ? true
                          : item.hasHomeSubNav && onHomeSection
                            ? true
                            : isNavItemActive(pathname, item, isActive);
                      return `${drawerNavBase} ${active ? drawerNavActive : drawerNavInactive}`;
                    }}
                  >
                    {({ isActive }) => {
                      const highlighted =
                        (item.to === '/app/setting' && onSettingsPage) ||
                        (item.hasHomeSubNav && onHomeSection) ||
                        isNavItemActive(pathname, item, isActive);
                      return (
                        <>
                          <Icon
                            name={item.icon}
                            className={highlighted ? 'text-primary' : ''}
                            filled={highlighted}
                          />
                          <span className={highlighted ? 'text-primary' : ''}>{t(item.labelKey)}</span>
                        </>
                      );
                    }}
                  </NavLink>
                  {item.hasHomeSubNav ? (
                    <HomeScenarioSubNav variant="drawer" onNavigate={onClose} />
                  ) : null}
                  {item.to === '/app/setting' ? (
                    <div
                      className="soundpill-mobile-nav-drawer__sub-group flex flex-col gap-0.5 ml-4 pl-3 mb-1"
                      role="group"
                      aria-label={t('settingsDraft.title')}
                    >
                      {SETTINGS_CATEGORIES.map((cat) => {
                        const subActive = onSettingsPage && settingsTab === cat.id;
                        return (
                          <NavLink
                            key={cat.id}
                            to={settingsPath(cat.id)}
                            onClick={onClose}
                            className={`${drawerSubNavBase} ${
                              subActive ? drawerSubNavActive : drawerSubNavInactive
                            }`}
                          >
                            <Icon
                              name={cat.icon}
                              className={`text-lg ${subActive ? 'text-primary' : ''}`}
                            />
                            <span className={subActive ? 'text-primary' : ''}>{t(cat.labelKey)}</span>
                          </NavLink>
                        );
                      })}
                    </div>
                  ) : null}
                </Fragment>
              ))}
            </nav>

            <div className="soundpill-mobile-nav-drawer__footer shrink-0 px-4 pb-6 pt-2 flex flex-col gap-4">
              <button
                type="button"
                onClick={() => {
                  onClose();
                  openApp();
                }}
                className="w-full py-3 rounded-xl bg-transparent border border-primary text-primary font-headline text-[15px] font-semibold hover:bg-primary/10 transition-colors flex items-center justify-center gap-2"
              >
                <Icon name="download" className="text-lg" />
                {t('navigator.useApp')}
              </button>
            </div>
          </motion.aside>
        </>
      ) : null}
    </AnimatePresence>
  );
}
